// src/context/UserContext.js
import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Role } from '../utils/enums'; // Import Role enum
import { jwtDecode } from 'jwt-decode'; // Corrected import
import { BASE_URL, IS_PRERENDERING } from '../utils/config'; // For API calls
import { apiFetch } from '../utils/apiClient'; // Import apiFetch
import { getMyDiscounts } from '../services/discountService'; // Import discount service

export const UserContext = createContext();

const initialUser = {
  fullName: '',
  username: '', // Add username to initialUser
  email: '', // Add email to initialUser
  isLoggedIn: false,
  // isAdmin is now derived from role
  role: Role.Guest, // Default role
  token: null,
  preferences: {
    itemsPerPage: 12,
    subscribe: true,
    currency: 'ILS',
  },
  creditPoints: 0, // Add creditPoints to initialUser
  pointsOnHold: 0,
  savedProperties: [], // Add savedProperties to initialUser
  investmentProfile: {}, // Add investmentProfile to ensure it's always present
  discounts: [], // Add discounts to initialUser
};

export const UserProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(initialUser);
  const [hasLoadedFromLocalStorage, setHasLoadedFromLocalStorage] = useState(false);

  useEffect(() => {
    // This effect runs only on the client, after the initial render.
    // It hydrates the user state from localStorage.
    try {
      const storedUserString = localStorage.getItem('user');
      if (storedUserString) {
        const parsedStoredUser = JSON.parse(storedUserString);
        console.log('[UserContext] Hydrating from localStorage. Parsed data:', parsedStoredUser);
        const hydratedUser = {
          ...initialUser,
          ...parsedStoredUser,
          preferences: {
            ...initialUser.preferences,
            ...(parsedStoredUser.preferences || {}),
          },
          investmentProfile: {
            ...initialUser.investmentProfile,
            ...(parsedStoredUser.investmentProfile || {}),
          },
        };
        if (parsedStoredUser.role === undefined && parsedStoredUser.isAdmin !== undefined) {
          hydratedUser.role = parsedStoredUser.isAdmin ? Role.Admin : initialUser.role;
        }
        delete hydratedUser.isAdmin;
        console.log('[UserContext] Final hydrated user state:', hydratedUser);
        setUser(hydratedUser);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      // The user state is already initialUser, so no change is needed on error.
    } finally {
      setHasLoadedFromLocalStorage(true); // Indicate that localStorage check is complete
    }
  }, []); // The empty dependency array ensures this runs only once.

  // Save user's state to localStorage whenever it changes
  useEffect(() => {
    if (user.isLoggedIn && user.token) {
      // Save if logged in and token exists
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      // Remove if not logged in or no token (e.g., on logout)
      localStorage.removeItem('user');
    }
  }, [user]);

  // Effect to automatically change the UI language based on user preference.
  useEffect(() => {
    // Do not run this logic on the server or during the initial client-side hydration pass
    if (IS_PRERENDERING) {
      return;
    }

    const preferredLanguage = user.preferences?.uiLanguage;
    // Only change language if user is logged in, has a preference, and it's different from the current language,
    // AND the current language is still the default fallback (meaning useSiteLanguage hasn't set it from the URL).
    if (user.isLoggedIn && preferredLanguage && preferredLanguage !== i18n.language && i18n.language === i18n.options.fallbackLng[0]) {
      console.log(`[UserContext] User logged in and language is default. Changing to preferred: ${preferredLanguage}`);
      i18n.changeLanguage(preferredLanguage).catch((err) => {
        console.error('[UserContext] Failed to change language:', err);
      });
    }
  }, [user.isLoggedIn, user.preferences?.uiLanguage, i18n]);

  // Centralized logout function within UserContext, wrapped in useCallback
  const logoutUser = useCallback(() => {
    //    console.log('[UserContext] Logging out user.');
    setUser({
      ...initialUser, // This correctly resets all fields including nested preferences
    });
    localStorage.removeItem('refreshToken');
  }, []);

  // Wrapped in useCallback to prevent re-creation on every render
  const updateUser = useCallback((updates) => {
    setUser((prevUser) => {
      // Create a deep copy of the updates to avoid mutating the original object
      const newUpdates = JSON.parse(JSON.stringify(updates));

      // Merge preferences separately to handle nested structure
      const mergedPreferences = { ...prevUser.preferences, ...newUpdates.preferences };
      delete newUpdates.preferences; // Remove preferences from top-level merge

      const newUser = {
        ...prevUser,
        ...newUpdates,
        preferences: mergedPreferences,
      };
      return newUser;
    });
  }, []);
  // New centralized login handler
  const login = useCallback(
    (loginData, impersonatorId = null) => {
      if (!loginData || !loginData.user || !loginData.accessToken || !loginData.refreshToken) {
        console.error('[UserContext] Invalid data passed to login function.');
        logoutUser(); // Ensure a clean state if login data is bad
        return;
      }
      console.log('[UserContext] Logging in user with data:', loginData);
      localStorage.setItem('refreshToken', loginData.refreshToken);
      updateUser({
        ...loginData.user,
        isLoggedIn: true, // Explicitly set login status
        impersonatorId: impersonatorId, // Handle impersonation
        token: loginData.accessToken,
      });
    },
    [updateUser, logoutUser]
  );

  // New function to fetch the latest user data from the server
  const refreshUser = useCallback(async () => {
    if (!user.isLoggedIn || !user.token) {
      console.log('[UserContext] Cannot refresh, user not logged in.');
      return;
    }
    try {
      // This endpoint will be created in the backend to provide fresh user data.
      const response = await apiFetch(
        `${BASE_URL}/authUser/me`,
        { method: 'GET' }, // apiFetch handles headers
        user.token
      );
      if (!response.ok) {
        // This might happen if the token is expired or invalid.
        // The silent refresh logic will handle token expiry eventually.
        console.error(`[UserContext] Failed to refresh user data. Status: ${response.status}`);
        return;
      }

      const freshUserData = await response.json();
      // Update only the fields that can change asynchronously on the backend
      updateUser({
        creditPoints: freshUserData.creditPoints,
        pointsOnHold: freshUserData.pointsOnHold,
        savedProperties: freshUserData.savedProperties,
        preferences: freshUserData.preferences, // Use the full preferences object from the server
      });

      // Now, fetch the user's discounts
      const userDiscounts = await getMyDiscounts(user.token);
      updateUser({
        discounts: userDiscounts,
      });
    } catch (error) {
      console.error('[UserContext] Error during user refresh:', error);
    }
  }, [user.isLoggedIn, user.token, updateUser]);

  // Silent token refresh logic
  useEffect(() => {
    let refreshTimeoutId;

    const scheduleRefresh = () => {
      if (user.token) {
        try {
          const decodedToken = jwtDecode(user.token);
          const currentTime = Date.now() / 1000; // Current time in seconds
          const expiresIn = decodedToken.exp - currentTime; // Time until expiry in seconds

          // Refresh 30 seconds before expiry, or immediately if very close to expiry (min 1 second buffer)
          const refreshOffset = 30; // seconds
          let timeoutDuration = (expiresIn - refreshOffset) * 1000; // Convert to milliseconds

          if (timeoutDuration < 1000) {
            // If already very close to expiry or past
            timeoutDuration = 1000; // Try to refresh soon
          }
          //          console.log(`[UserContext] Access token expires in: ${expiresIn.toFixed(0)}s. Scheduling refresh in ${(timeoutDuration / 1000).toFixed(0)}s.`);

          refreshTimeoutId = setTimeout(async () => {
            //            console.log('[UserContext] Attempting silent token refresh...');
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              //              console.log('[UserContext] No refresh token found. Logging out.');
              logoutUser();
              return;
            }

            try {
              const response = await apiFetch(
                `${BASE_URL}/authUser/refresh-token`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ refreshToken }),
                },
                null // No auth token needed for refresh-token endpoint itself
              );

              if (!response.ok) {
                console.log('[UserContext] Silent refresh failed. Logging out.');
                logoutUser();
                throw new Error('Failed to refresh token silently');
              }

              const { accessToken: newAccessToken } = await response.json();
              //              console.log('[UserContext] Silent refresh successful. New access token obtained.');
              updateUser({ token: newAccessToken }); // This will trigger this useEffect again
            } catch (error) {
              console.error('[UserContext] Error during silent refresh:', error);
              logoutUser();
            }
          }, timeoutDuration);
        } catch (error) {
          console.error('[UserContext] Error decoding token or scheduling refresh:', error);
          logoutUser(); // If token is invalid, log out
        }
      }
    };

    scheduleRefresh();

    return () => {
      clearTimeout(refreshTimeoutId); // Cleanup timeout on unmount or token change
    };
  }, [user.token, logoutUser, updateUser]); // Re-run when token changes

  // Wrapped in useCallback for stability
  const updateToken = useCallback(
    (newToken) => {
      updateUser({ token: newToken });
    },
    [updateUser]
  );

  const send2faCode = useCallback(async (payload) => {
    // payload: { userId, method }
    const response = await apiFetch(
      `${BASE_URL}/authUser/2fa/send-code`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      null // No user token needed for this step
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send 2FA code.');
    }
    return data; // Returns { message: '...' } on success
  }, []);

  const verify2faCode = useCallback(
    async (payload) => {
      // payload: { userId, method, code, trustDevice }
      const response = await apiFetch(
        `${BASE_URL}/authUser/2fa/verify-code`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // Send cookies with the request
          body: JSON.stringify(payload),
        },
        null // No user token needed
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify 2FA code.');
      }
      return data; // Returns { accessToken, refreshToken, user } on success
    },
    [] // No dependencies, as it just makes an API call and returns data
  );

  const setupAuthenticator = useCallback(
    async (token) => {
      // No payload needed, just the temporary 2FA token in the header
      const response = await apiFetch(
        `${BASE_URL}/authUser/2fa/setup-authenticator`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
        token // Use the temporary setup token for auth
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to start authenticator setup.');
      }
      return data; // Returns { secret, qrCodeUrl }
    },
    [] // No dependency on user.token
  );

  const verifyAuthenticatorSetup = useCallback(
    async (payload) => {
      // payload: { code, secret, token }
      const { token, ...bodyPayload } = payload;
      const response = await apiFetch(
        `${BASE_URL}/authUser/2fa/verify-authenticator-setup`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // Add this to allow setting cookies
          body: JSON.stringify(bodyPayload),
        },
        token // Use the temporary setup token
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify authenticator setup.');
      }
      return data; // Returns { accessToken, refreshToken, user }
    },
    [] // No dependency on user.token
  );

  const disableAuthenticator = useCallback(async (payload) => {
    // payload: { userId, method, code }
    const response = await apiFetch(
      `${BASE_URL}/authUser/2fa/disable-authenticator`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // No auth token needed as this is part of the login flow
        body: JSON.stringify(payload),
      },
      null
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to disable authenticator.');
    }
    return data; // Returns { message: '...' }
  }, []);

  // The context value now derives isAdmin on the fly
  const contextValue = useMemo(
    () => ({
      user: {
        ...user,
        isAdmin: user.role === Role.Admin,
        isSeller: user.role === Role.Seller || user.role === Role.Admin,
      },
      hasLoadedFromLocalStorage, // Expose the new flag
      login, // Expose the new login handler
      updateUser,
      updateToken,
      logoutUser, // Expose logoutUser
      refreshUser, // Expose the new refresh function
      send2faCode,
      verify2faCode,
      setupAuthenticator,
      verifyAuthenticatorSetup,
      disableAuthenticator,
    }),
    [user, hasLoadedFromLocalStorage, login, updateUser, updateToken, logoutUser, refreshUser, send2faCode, verify2faCode, setupAuthenticator, verifyAuthenticatorSetup, disableAuthenticator]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
